import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  Input,
  OnInit,
  Signal,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { BoTableComponent } from '@components/bo-table/bo-table.component';
import { ElementErrorDirective } from '@components/element-widgets/element-error/element-error.directive';
import { FondsFilterSelections } from '@components/element-widgets/fonds-filters/fonds-filters';
import { FondsFiltersComponent } from '@components/element-widgets/fonds-filters/fonds-filters.component';
import { RouteSelectedFund } from '@components/fondsportrait/selected-fund';
import { NoSearchResultsComponent } from '@components/no-search-results/no-search-results.component';
import { TranslocoModule } from '@ngneat/transloco';
import { SpacerComponent } from 'src/app/common/components/spacer/spacer.component';
import { SdSignalModelDirective } from 'src/app/common/directives/sd-signal-model.directive';
import iconLibrary from 'src/app/common/iconLibrary';
import { ActionFunctions, TableConstructionData } from 'src/app/table-handling';
import { CUID } from 'src/environments/cuids';
import { DOCUMENT_CONSTANTS } from 'src/environments/document-constants';
import { AdvancedFondListDrawerComponent } from './advanced-fond-list-drawer/advanced-fond-list-drawer.component';

export type FundsVisibility = 'public' | 'special' | 'user';

interface RouteParams {
  searchTerm?: string;
  viewFunds?: string;
}

@Component({
  selector: 'wct-advanced-fond-list',
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    BoTableComponent,
    FondsFiltersComponent,
    NoSearchResultsComponent,
    SpacerComponent,
    AdvancedFondListDrawerComponent,
    SdSignalModelDirective,
    ElementErrorDirective,
  ],
  templateUrl: './advanced-fond-list.component.html',
  styleUrl: './advanced-fond-list.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AdvancedFondListComponent implements OnInit {
  @Input({ required: false }) public userFundsSelectionVisible: boolean = true; // currently used to identify if we are in user or prelogin context
  @Input({ required: false }) public initialFundsVisibilitySelection: FundsVisibility = 'special';
  @Input({ required: false }) public controlComponentViaUrl: boolean = true;
  @Input({ required: false }) public redirectUrl: string = '';

  @ViewChild(AdvancedFondListDrawerComponent, { static: true }) protected drawer: AdvancedFondListDrawerComponent;

  protected readonly selection: WritableSignal<{ selections: FondsFilterSelections }> = signal({ selections: {} });
  protected readonly searchTerm: WritableSignal<string> = signal('');

  protected readonly hadDataBefore: Signal<boolean> = computed(() => {
    const key: string = this.fundsVisibility();
    const count: number | undefined = this.initialDataCount()[key];
    return count > 0;
  });

  protected readonly hiddenFilters: Signal<string[]> = signal([]);

  protected readonly selectedFilters: Signal<SelectedFilter[]> = computed(() => {
    const selection: FondsFilterSelections = this.selection().selections;
    return selection ? this.getSelectedFilters(selection) : [];
  });

  protected readonly tableFilterSelections: Signal<FondsFilterSelections> = computed(() => ({
    ...this.selection().selections,
  }));

  protected readonly iconLibrary = iconLibrary;

  protected readonly fundsVisibility : WritableSignal<FundsVisibility> = signal(this.initialFundsVisibilitySelection);

  protected readonly cuid: Signal<string> = computed(() => {
    switch(this.fundsVisibility()) {
      case "user":
        return CUID.advancedFondList.user;
      case "public":
        return CUID.advancedFondList.public;
      case "special":
        return CUID.advancedFondList.special;
    }
  });

  protected readonly initialDataCount: WritableSignal<Record<string, number>> = signal({});

  private readonly routeSelectedFund: RouteSelectedFund = inject(RouteSelectedFund);

  public actionHandler: ActionFunctions = {
    openFund: (funds: string) => {
      this.drawer.show(funds);
    },
  };

  protected selectedFund: string;

  ngOnInit(): void {
    this.fundsVisibility.set(this.initialFundsVisibilitySelection);
    this.selectedFund = this.routeSelectedFund.refresh().get();

    if (this.controlComponentViaUrl) {
      const routeParams: RouteParams = this.getRouteParamss();

      if (routeParams.searchTerm) {
        this.searchTerm.set(routeParams.searchTerm);
      }

      if (routeParams.viewFunds === 'public' || routeParams.viewFunds === 'all') {
        this.fundsVisibility.set('public');
      } else if (routeParams.viewFunds === 'special') {
        this.fundsVisibility.set('special');
      } else if (routeParams.viewFunds === 'user') {
        this.fundsVisibility.set('user');
      }
    }

    this.updateSelectionSignal();
  }

  protected removeSelectedFilter(groupName: string): void {
    delete this.selection().selections[groupName];
    this.updateSelectionSignal();
  }

  protected resetSearchAndFilters(): void {
    this.searchTerm.set('');
    this.selection.set({
      selections: {}
    });
  }

  private getRouteParamss(): RouteParams {
    const searchParams = new URLSearchParams(window.location.search);
    return {
      searchTerm: searchParams.get('searchTerm'),
      viewFunds: searchParams.get('viewFunds'),
    };
  }

  private getSelectedFilters(selection: FondsFilterSelections): SelectedFilter[] {
    return Object.entries(selection)
      .map(([groupName, values]) => {
        return {
          groupName,
          valuesText: Array.isArray(values) ? values.join(', ') : values,
        };
      });
  }

  private updateSelectionSignal(): void {
    this.selection.update(selection => ({ selections: selection.selections }));
  }

  protected checkLoadedData(data: TableConstructionData): void {
    const initalDataCount: Record<string, number> = this.initialDataCount();
    const key: string = this.fundsVisibility();

    if (key in initalDataCount) {
      return;
    }

    this.initialDataCount.set({
      ...initalDataCount,
      [key]: data.rows.length
    });
  }
}

interface SelectedFilter {
  groupName: string;
  valuesText: string;
}
